import { DigitalTwinSettingCreationRequest, TimeSeriesSettingValue } from 'api/digitalTwin/digitalTwin.api'
import { postStat } from 'api/stats/stats.api'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  checkScrollDirection,
  getCopiedDatasetSettingValues,
  handleMouseMove,
  setScrollDirection,
  shouldClearTimeSeriesSelectionOnDocumentMouseDown,
  updateTimeSeriesValues,
} from 'ui/components/DigitalTwinSettingForm/timeSeriesSettingTable.helper'
import { TimeSeriesDataTableColumnDef } from 'ui/components/TimeSeriesDataTable/types'
import DatasetTable from 'ui/uiConfig/components/DatasetTable/DatasetTable'
import Datetime from 'utils/datetime/datetime'
import { getPrettyName, getPropertyPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { CalculateTimeSeriesSettingsForm, ParseResult } from './CalculateTimeSeriesSettingsForm'
import { MODIFY_SETTING_COLUMN_RETURN_ID } from './constants'
import createEditableColumnFactory from './editableColumnFactory'
import { TimeSeriesTableHelpButton } from './TimeSeriesTableHelpDialog/TimeSeriesTableHelpButton'
import { getAvailableTimeSeriesTableHelpTabIds } from './TimeSeriesTableHelpDialog/timeSeriesTableHelp.helper'
import { TimeSeriesActiveEdit, TimeSeriesCellDrag, TimeSeriesSettingTableProps } from './types'
import { useCustomCheckboxColumn } from './useCustomCheckboxColumn'
import { useRowSelection } from './useRowSelection'
import { useShiftKeySelected } from './useShiftKeySelected'

export function getSettingData(
  settingData: DigitalTwinSettingCreationRequest[],
  index: number
): DigitalTwinSettingCreationRequest | undefined {
  const setting = settingData.at(index)
  if (setting) {
    if (setting.isTimeSeries && typeof setting.value === 'object') {
      return setting
    }
  }

  return undefined
}

export function getTableKey(values: { id?: number; value: number | null }[]): string {
  return `${values.reduce((v, s) => `(${s.id},${s.value}),${v}`, '')}`
}

export default function TimeSeriesSettingTable({
  chartItems,
  datasets,
  dialogContentRef,
  invalidValuesIndex,
  setSettingData,
  settingsData,
  digitalTwinTranslations,
  times,
  tableKey,
  setTableKey,
  enableComputableSettings,
  disabled,
}: TimeSeriesSettingTableProps): ReactElement {
  const [mouseDownValue, setMouseDownValue] = useState<TimeSeriesCellDrag | null>(null)
  const [activeEdit, setActiveEdit] = useState<TimeSeriesActiveEdit | null>(null)
  const [mouseOverIndex, setMouseOverIndex] = useState<number | null>(null)
  const [intervalId, setIntervalId] = useState<ReturnType<typeof setInterval> | null>(null)
  const [focusedCell, setFocusedCell] = useState<{ row: number; column: string }>({ row: 0, column: '' })
  const shiftKeyPressed = useShiftKeySelected()
  const { selectedRows, setSelectedRows, toggleRowSelection, selectRowByClick } = useRowSelection()

  // This component was originally written under the assumption that all datasets have whole hour intervals,
  // but this is no longer true since we added support for finer time intervals to support 15-minute
  // electricity market.
  // Since we still only support timeseries settings with whole hour intervals, we filter out all non-whole-hour
  // data points from the datasets used in this component. This means some data is lost for datasets with
  // finer intervals, but at least the component works as intended without a major rewrite.
  const wholeHourDatasets = useMemo(() => {
    return datasets.map((dataset) => {
      const wholeHourTimes: Array<ISODateTime> = []
      const wholeHourValues: Array<number | null> = []
      dataset.times.forEach((time, index) => {
        const datetime = new Datetime(time)
        if (datetime.date.getMinutes() === 0) {
          wholeHourTimes.push(datetime.ISOString)
          wholeHourValues.push(dataset.values[index])
        }
      })
      return {
        ...dataset,
        times: wholeHourTimes,
        values: wholeHourValues,
      }
    })
  }, [datasets])

  const { t } = useTranslation()

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const calculateFormRef = useRef<HTMLDivElement>(null)

  // This is necessary to get an updated value inside the interval function, where we update the scroll amount.
  const scrollDirectionRef = useRef<'none' | 'up' | 'down'>('none')

  const handleUpdateValues = useCallback(
    (newValues: TimeSeriesSettingValue, index: number) => {
      updateTimeSeriesValues(
        newValues,
        index,
        settingsData,
        getSettingData,
        times,
        getTableKey,
        setTableKey,
        setSettingData
      )
    },
    [settingsData, times, setSettingData, setTableKey]
  )

  const copyDatasetToSettingValues = useCallback(
    (dataset: Dataset, index: number, selectedRowIndices?: number[]) => {
      const settingData = getSettingData(settingsData, index)
      if (settingData === undefined) {
        return
      }

      const newSettingValues = getCopiedDatasetSettingValues(
        settingData.value as TimeSeriesSettingValue,
        dataset,
        selectedRowIndices
      )

      handleUpdateValues(newSettingValues, index)
    },
    [settingsData, handleUpdateValues]
  )

  useEffect(() => {
    const handleMouseUp = (): void => {
      // Clear value being dragged.
      setMouseDownValue(null)
      setMouseOverIndex(null)

      // Stop scrolling.
      scrollDirectionRef.current = 'none'
      if (intervalId !== null) {
        clearInterval(intervalId)
        setIntervalId(null)
      }
    }

    window.addEventListener('mouseup', handleMouseUp)

    // Clear current listener before registering a new one, or when component unmounts.
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [intervalId])

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent): void => {
      if (
        shouldClearTimeSeriesSelectionOnDocumentMouseDown(
          event.target,
          tableContainerRef.current,
          calculateFormRef.current
        )
      ) {
        setSelectedRows([])
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [setSelectedRows])

  const handleMouseMoveWrapper = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      handleMouseMove(
        e,
        mouseDownValue,
        tableContainerRef,
        dialogContentRef,
        scrollDirectionRef,
        intervalId,
        setIntervalId
      )
    },
    [mouseDownValue, intervalId, dialogContentRef]
  )

  const handleMouseEvent = useCallback(
    (event: React.MouseEvent<Element>, newMouseDownValue: TimeSeriesCellDrag | null): void => {
      if (event.button === 0) {
        setMouseDownValue(newMouseDownValue)
        if (newMouseDownValue !== null) {
          // Mouse button might have been pressed at the top or bottom, so check if we should start scrolling immediately.
          checkScrollDirection(
            event.nativeEvent,
            tableContainerRef,
            dialogContentRef,
            scrollDirectionRef,
            intervalId,
            setIntervalId
          )
        } else {
          // Mouse up, stop scrolling.
          setScrollDirection('none', scrollDirectionRef, intervalId, setIntervalId, dialogContentRef, tableContainerRef)
        }
      }
    },
    [dialogContentRef, intervalId]
  )

  const columnOverrides = useMemo(() => {
    const overrides: { [key: string]: { enableEditing: boolean } } = {}

    chartItems.forEach((item) => {
      overrides[item.data_id] = {
        enableEditing: false,
      }
    })

    return overrides
  }, [chartItems])

  const cellRefs = useRef<{ [key: string]: React.RefObject<HTMLInputElement> }>({})
  useEffect(() => {
    const key = `${focusedCell.column}-${focusedCell.row}`
    cellRefs.current[key]?.current?.focus()
  }, [focusedCell])

  const editableColumnFactory = useMemo(
    () =>
      createEditableColumnFactory({
        datasets: wholeHourDatasets,
        chartItems,
        invalidValuesIndex,
        activeEdit,
        mouseDownValue,
        mouseOverIndex,
        focusedCell,
        times,
        settingsData,
        t,
        copyDatasetToSettingValues,
        selectedRows,
        handleMouseEvent,
        setActiveEdit,
        setFocusedCell,
        setMouseOverIndex,
        setMouseDownValue,
        setSelectedRows,
        selectRowByClick,
        updateTimeSeriesValues: handleUpdateValues,
        setSettingData,
        cellRefs,
        disabled,
      }),
    [
      chartItems,
      copyDatasetToSettingValues,
      wholeHourDatasets,
      handleMouseEvent,
      invalidValuesIndex,
      selectedRows,
      activeEdit,
      mouseDownValue,
      mouseOverIndex,
      setSelectedRows,
      selectRowByClick,
      setSettingData,
      settingsData,
      t,
      times,
      handleUpdateValues,
      focusedCell,
      disabled,
    ]
  )

  const createEditableColumnOverrides = useCallback(
    (title: string, returnId: string, unit?: string, index = 0): TimeSeriesDataTableColumnDef => {
      return {
        Header: editableColumnFactory.Header({ title, returnId, unit, index }),
        muiTableBodyCellProps: editableColumnFactory.muiTableBodyCellProps({ index }),
        muiEditTextFieldProps: editableColumnFactory.muiEditTextFieldProps({
          index,
          returnId,
        }),
      }
    },
    [editableColumnFactory]
  )

  const editableColumnsOverrides = useMemo(() => {
    const overrides: { [key: string]: TimeSeriesDataTableColumnDef } = {}
    settingsData?.forEach((settingData, index) => {
      const prettyName = getPropertyPrettyName(settingData.name, settingData.attribute, digitalTwinTranslations)
      overrides[settingData.attribute] = createEditableColumnOverrides(
        prettyName,
        settingData.attribute,
        settingData.unit,
        index
      )
    })
    return overrides
  }, [digitalTwinTranslations, createEditableColumnOverrides, settingsData])

  const filteredDatasets = useMemo(
    () =>
      wholeHourDatasets
        .filter(
          (dataset) =>
            !!dataset.return_id &&
            dataset.return_id !== MODIFY_SETTING_COLUMN_RETURN_ID &&
            dataset.values.filter((value) => value !== null).length > 0
        )
        .sort((a, b) => {
          const aIsOutcome = a.return_id?.toLowerCase().includes('outcome') ? 1 : 0
          const bIsOutcome = b.return_id?.toLowerCase().includes('outcome') ? 1 : 0
          return aIsOutcome - bIsOutcome
        }),
    [wholeHourDatasets]
  )
  const hasDataColumns = filteredDatasets.length > 0

  const [customColumns, settingsDataIndicesToComputed, resetSettingsDataIndicesToComputed] = useCustomCheckboxColumn({
    datasets: wholeHourDatasets,
    enableComputableSettings,
    hasDataColumns,
    selectedRows,
    setSelectedRows,
    toggleRowSelection,
    shiftKeyPressed,
  })

  const onCalculateClick = useCallback(
    (parseResult: ParseResult) => {
      const settingData = getSettingData(settingsData, 0)
      if (typeof parseResult.selectedDataset === 'undefined' || typeof settingData?.value !== 'object') {
        return
      }

      const newSettingValues = { ...(settingData.value as TimeSeriesSettingValue) }
      settingsDataIndicesToComputed
        .map((index) => {
          const value = parseResult.selectedDataset.values[index]
            ? parseResult.percentageInput
              ? parseResult.selectedDataset.values[index] * parseResult.percentageInput
              : parseResult.mwInput
                ? parseResult.selectedDataset.values[index] + parseResult.mwInput
                : null
            : null
          return {
            time: parseResult.selectedDataset.times[index] as ISODateTime,
            value: value,
          }
        })
        .forEach((computedSetting) => {
          newSettingValues[computedSetting.time] = {
            id: newSettingValues[computedSetting.time].id,
            value: computedSetting.value,
          }
        })

      postStat(
        'timeseries-setting',
        `calculate-new-values-with-${
          parseResult.percentageInput ? `${parseResult.percentageInput}%` : `${parseResult.mwInput}MW`
        }`
      ) //DATA LOG FOR PILOT 1,5
      handleUpdateValues(newSettingValues, 0)
      resetSettingsDataIndicesToComputed()
    },
    [resetSettingsDataIndicesToComputed, settingsData, settingsDataIndicesToComputed, handleUpdateValues]
  )

  const showCalculateForm = Boolean(enableComputableSettings) && hasDataColumns

  const helpTabIds = getAvailableTimeSeriesTableHelpTabIds({
    editingEnabled: !disabled,
    canCopyFromOtherDataset: wholeHourDatasets.some((dataset) => dataset.return_id !== MODIFY_SETTING_COLUMN_RETURN_ID),
    canCalculate: showCalculateForm,
  })
  const showHelpButton = helpTabIds.length > 0

  return (
    <>
      {(showHelpButton || showCalculateForm) && (
        <div className={`flex items-center justify-end ${showCalculateForm ? 'mb-[30px]' : 'mb-2'}`}>
          {showHelpButton && (
            <TimeSeriesTableHelpButton availableTabIds={helpTabIds} hasSelectionCheckboxes={showCalculateForm} />
          )}
          {showHelpButton && showCalculateForm && <div className="mx-3 h-7 w-px shrink-0 bg-grey-4" />}
          {showCalculateForm && (
            <CalculateTimeSeriesSettingsForm
              datasets={wholeHourDatasets}
              filteredDatasets={filteredDatasets}
              chartItems={chartItems}
              disableCalculateButton={settingsDataIndicesToComputed.length === 0 || disabled}
              disableInputFields={disabled}
              onCalculateClick={onCalculateClick}
              containerRef={calculateFormRef}
            />
          )}
        </div>
      )}
      <DatasetTable
        tableContainerRef={tableContainerRef}
        items={chartItems.map((item) => {
          return {
            title: item.title,
            data_id: item.data_id,
            unit: item.unit ?? '',
            decimals: item.decimals ?? 2,
            table_order: item.order ?? 0,
          }
        })}
        sorting_function={'table_order'}
        customColumns={customColumns}
        datasets={wholeHourDatasets}
        columnOverrides={{
          ...columnOverrides,
          ...editableColumnsOverrides,
          modified_setting_value: createEditableColumnOverrides(
            `${getPrettyName(settingsData[0].name, digitalTwinTranslations)}: ${getPrettyName(settingsData[0].attribute, digitalTwinTranslations)}`,
            'modified_setting_value',
            settingsData[0].unit
          ),
        }}
        tableKey={tableKey}
        children={undefined}
        title=""
        do_not_show_zero_or_null_values_in_table={false}
        do_not_show_download_button
        do_not_show_sum_or_average_row
        do_not_show_top_toolbar
        do_not_show_min_max_row
        show_only_value_in_future={false}
        show_initial_page_with_current_optimization_hour={false}
        enableEditing={!disabled}
        enableColumnActions={false}
        onMouseMove={handleMouseMoveWrapper}
        disableCustomSorting
        hideSingleValueDatasets={false}
        onPaginationChange={resetSettingsDataIndicesToComputed}
      />
    </>
  )
}
